import type { BookRequestCheckboxStatuses } from "@/features/acquireMultipleBooks/types";
import { useApiClient } from "@/hooks/useApiClient";
import React, { useEffect } from "react";
import { useFeatureFlags } from "@/configs/featureFlag";
import { UserConfigGetAPI } from "@/features/accountSettings/api/UserConfigAPI";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Typography,
  Link,
} from "@mui/material";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { usePictureRequestAmount } from "@/api/usePictureRequestAmount";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";

import { formatNumberWithComma } from "@/utils/formatNumber";

const MANUAL_LINK_URL =
  "https://trustart.notion.site/4-1d114754f2df806f9991e47fdee087bd#1d114754f2df81008d8cc6cefc792f78";

interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  checkBoxOptions: BookRequestCheckboxStatuses[];
  handleAcquireButtonClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    cancelLoading: () => void
  ) => void;
  disableConfirmModalCancelButton?: boolean;
  isReadOnly?: boolean;
}

export const ConfirmAcquireMultipleBooksDialog: React.FC<IProps> = (
  props: IProps
) => {
  const {
    open,
    setOpen,
    checkBoxOptions,
    handleAcquireButtonClick,
    disableConfirmModalCancelButton = false,
    isReadOnly = false,
  } = props;
  // ユーザコンフィグを取得するフック
  const userConfig = UserConfigGetAPI();

  // feature flagの取得
  const { displayChargePriceInRequestModal } = useFeatureFlags();

  // 不動産登記の金額を取得するフック
  const { apiClient } = useApiClient();
  const { data: pictureRequestAmount } = usePictureRequestAmount(apiClient);

  // 不動産登記の金額が全て取得できたかどうかのフラグ
  const [isLoadedPictureRequestAmount, setIsLoadedPictureRequestAmount] =
    React.useState(false);
  // NOTE: システム的に基本的に金額が取得できないことはない
  useEffect(() => {
    if (
      pictureRequestAmount?.shoyushajiko &&
      pictureRequestAmount?.zenbujiko &&
      pictureRequestAmount?.chisekisokuryozu &&
      pictureRequestAmount?.denshikozu &&
      pictureRequestAmount?.chiekikenzumen &&
      pictureRequestAmount?.tatemonozumen
    ) {
      setIsLoadedPictureRequestAmount(true);
    } else {
      setIsLoadedPictureRequestAmount(false);
    }
  }, [pictureRequestAmount]);

  // isCheckedSome関数のキーの型を定義する
  type CheckKey =
    | "owner"
    | "allMatters"
    | "tisekiDrawings"
    | "tiekiDrawings"
    | "electricDrawings"
    | "buildingDrawings";

  // チェックボックスの状態を確認する関数
  const isCheckedSome = (
    checkBoxOptions: BookRequestCheckboxStatuses[],
    key: CheckKey
  ): boolean => {
    return checkBoxOptions.some((option) => {
      switch (key) {
        case "owner":
          return option.ownerInfo.checked;
        case "allMatters":
          return (
            option.kyoutan.checked ||
            option.sintaku.checked ||
            option.genzai.checked ||
            option.requestClosureBook.checked
          );
        case "tisekiDrawings":
          return option.tisekiDrawings.checked;
        case "tiekiDrawings":
          return option.tiekiDrawings.checked;
        case "electricDrawings":
          return option.electricDrawings.checked;
        case "buildingDrawings":
          return option.buildingDrawings.checked;
        default:
          return false;
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        setOpen(false);
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"確認画面"}</DialogTitle>
      <DialogContent>
        {displayChargePriceInRequestModal &&
        isLoadedPictureRequestAmount &&
        userConfig !== undefined &&
        !userConfig.isPriceHidden ? (
          <DialogContentText id="alert-dialog-description" component="div">
            <Typography>登記/図面の取得を開始します。</Typography>
            <Typography>
              取得件数に対して、1件あたり以下の料金が発生します。
            </Typography>
            <Box component="ul" sx={{ pl: 5, my: 2 }}>
              {isCheckedSome(checkBoxOptions, "owner") && (
                <Typography component="li">
                  所有者事項：
                  {formatNumberWithComma(pictureRequestAmount?.shoyushajiko)}円
                </Typography>
              )}
              {isCheckedSome(checkBoxOptions, "allMatters") && (
                <Typography component="li">
                  全部事項：
                  {formatNumberWithComma(pictureRequestAmount?.zenbujiko)}円
                </Typography>
              )}
              {isCheckedSome(checkBoxOptions, "tisekiDrawings") && (
                <Typography component="li">
                  地積測量図：
                  {formatNumberWithComma(
                    pictureRequestAmount?.chisekisokuryozu
                  )}
                  円
                </Typography>
              )}
              {isCheckedSome(checkBoxOptions, "electricDrawings") && (
                <Typography component="li">
                  電子公図：
                  {formatNumberWithComma(pictureRequestAmount?.denshikozu)}円
                </Typography>
              )}
              {isCheckedSome(checkBoxOptions, "tiekiDrawings") && (
                <Typography component="li">
                  地役権図面：
                  {formatNumberWithComma(pictureRequestAmount?.chiekikenzumen)}
                  円
                </Typography>
              )}
              {isCheckedSome(checkBoxOptions, "buildingDrawings") && (
                <Typography component="li">
                  建物図面/各階平面図：
                  {formatNumberWithComma(pictureRequestAmount?.tatemonozumen)}円
                </Typography>
              )}
            </Box>
            <Typography>
              ※取得済登記・リユース登記となる場合は価格が異なります。
            </Typography>
            <Typography>
              詳細は
              <Link
                href={MANUAL_LINK_URL}
                target="_blank"
                rel="noreferrer"
                sx={{ mx: 0.5 }}
              >
                マニュアル
              </Link>
              をご確認ください。
            </Typography>
          </DialogContentText>
        ) : (
          <DialogContentText id="alert-dialog-description" component="div">
            <Typography>【課金が発生します】登記/図面を取得します。</Typography>
            <Typography>
              ※ 画面の切り替わりまでに時間がかかる場合があります
            </Typography>
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
        <Button
          variant={ButtonVariantOption.Outlined}
          onClick={() => {
            setOpen(false);
          }}
          disabled={disableConfirmModalCancelButton}
          label={"キャンセル"}
        />
        <ReadOnlyTooltip isReadOnly={isReadOnly}>
          <SubmitButton
            variant={ButtonVariantOption.Contained}
            onClick={(event, cancelLoading) => {
              handleAcquireButtonClick(event, cancelLoading);
            }}
            label={"取得する"}
            timeout={120000}
            disabled={isReadOnly}
          />
        </ReadOnlyTooltip>
      </DialogActions>
    </Dialog>
  );
};
